import { BlockBlobClient, newPipeline } from '@azure/storage-blob';
import { strIsNullOrEmpty, tryParseJSONObject } from '@fox/atoms';
import { IWorkBenchLayout } from '@fox/blocks';
import {
	DEFAULT_POINT_TYPE,
	filterOutConditionals,
	FormQuestionTypes,
	IQuestionAnswer,
	ISection,
	QuestionBlockMetadata,
	SwiftFoxSearchResult,
	UploadedFilesWithMeta
} from '@fox/forms-runtime';
import { GetFormSasInputModel } from '../../Membership/goals/contracts';
import { addContentId, mutateHelper, queryHelper } from './fetch-templates';
export async function fetchPointsTotal(input: {
	requestToken: string;
	contentId: string;
	sections: ISection[];
	layout: IWorkBenchLayout | undefined;
	answers: IQuestionAnswer[];
}): Promise<{ status: 'success' | 'error'; pointTotal?: number; message?: string }> {
	const { answers, layout, sections, contentId, requestToken } = input;
	const filteredAnswers = filterOutConditionals(layout, answers, sections);

	const pointsInfo = await calculateQuestionPoints(filteredAnswers, sections, requestToken, contentId);
	if (!pointsInfo) {
		console.log('Error calculating points total: No points information found');
		return { status: 'error', message: 'An unexpected error occured.' };
	}
	const pointTotal = pointsInfo.reduce((acc, cur) => acc + cur.value, 0);

	return { status: 'success', pointTotal };
}
export async function complete(input: {
	contentId: string;
	requestToken: string;
	searchString: string;
	types?: string[];
	categories?: string[];
	signedParams?: string;
}): Promise<SwiftFoxSearchResult[] | undefined> {
	const { searchString, types, categories, contentId, requestToken } = input;
	let url = `/Umbraco/Api/Membership/SwiftFoxEntities?${addContentId(contentId)}`;
	if (types?.length) {
		url += types.map(type => `&types=${encodeURIComponent(type)}`).join('');
	}
	if (categories?.length) {
		url += categories.map(type => `&categories=${encodeURIComponent(type)}`).join('');
	}
	if (searchString?.length) {
		url += `&searchString=${encodeURIComponent(searchString)}`;
	}

	const res = await queryHelper<SwiftFoxSearchResult[]>(requestToken, url);
	if (res.error) {
		throw new Error(res.error);
	}
	return res.data;
}
const pipeline = newPipeline();
export async function uploadFile(input: {
	requestToken: string;
	contentId: string;
	file: File;
	formId: number;
}): Promise<UploadedFilesWithMeta | undefined> {
	const { file, formId, requestToken, contentId } = input;
	const url = '/Umbraco/Api/Membership/GetFormAttachmentSas';
	const res = await mutateHelper<string, GetFormSasInputModel>({ contentId, fileName: file.name, surveyId: formId }, requestToken, url);

	if (!res?.data || res.error) {
		alert('Unable to upload the file at this time');
		return;
	}
	const blobClient = new BlockBlobClient(res.data, pipeline);
	await blobClient.uploadData(await file.arrayBuffer(), {
		onProgress(progress) {
			console.log('Uploading... ', file.size, progress.loadedBytes);
		}
	});

	return {
		fileName: file.name,
		mime: file.type,
		url: blobClient.url.split('?')[0],
		size: file.size
	};
}

const QUESTION_TYPES_WITH_POINTS = [
	FormQuestionTypes.SingleChoice,
	FormQuestionTypes.SingleChoiceWithOther,
	FormQuestionTypes.MultiChoice,
	FormQuestionTypes.MultiChoiceWithOther,
	FormQuestionTypes.Number
];

const decrypt = async (cryptoKey: CryptoKey, encryptedData: string): Promise<string> => {
	if (strIsNullOrEmpty(encryptedData)) return encryptedData;
	if (!cryptoKey) {
		throw new Error('Decryption key is unavailable.');
	}

	const encryptedArray = new Uint8Array(
		atob(encryptedData)
			.split('')
			.map(char => char.charCodeAt(0))
	);
	const iv = encryptedArray.slice(0, 12);
	const encryptedBytes = encryptedArray.slice(12);

	// Perform decryption directly with the CryptoKey
	const decrypted = await crypto.subtle.decrypt(
		{
			name: 'AES-GCM',
			iv: iv
		},
		cryptoKey,
		encryptedBytes
	);

	const decodedData = new TextDecoder().decode(decrypted);
	return decodedData;
};

const getFormEncryptionKey = async (contentId: string, requestToken: string) => {
	const url = `/Umbraco/Api/Membership/FormEncryptionKey?${addContentId(contentId)}`;
	const result = await queryHelper<string>(requestToken, url);
	const encodedKey = result?.data;
	if (encodedKey) {
		const decodeBase64 = (base64: string): ArrayBuffer => Uint8Array.from(atob(base64), c => c.charCodeAt(0)).buffer;

		const importKey = await crypto.subtle.importKey('raw', decodeBase64(encodedKey), { name: 'AES-GCM', length: 256 }, true, [
			'encrypt',
			'decrypt'
		]);
		return importKey;
	}
};

const calculateQuestionPoints = async (answers: IQuestionAnswer[], sections: ISection[], requestToken: string, contentId: string) => {
	const encryptionKey = await getFormEncryptionKey(contentId, requestToken);
	if (!encryptionKey) return;
	const allFields = sections.flatMap(s => s.fields);

	const pointMapping: { questionName: string; value: number; type: string }[] = [];
	for (const answer of answers ?? []) {
		const match = allFields.find(f => f.name === answer.questionName);
		if (match && QUESTION_TYPES_WITH_POINTS.includes(match.type as FormQuestionTypes)) {
			try {
				const meta = tryParseJSONObject<QuestionBlockMetadata>(match.metadataJson, {});
				if (!meta.points?.enablePoints) continue;

				switch (match.type) {
					case FormQuestionTypes.SingleChoice:
					case FormQuestionTypes.SingleChoiceWithOther:
						const matchingChoice = meta.choices?.find(c => c.value === answer.answer?.answerString);
						const matchingPoints = meta?.points?.pointMappings?.find(p => p.requiredValue === matchingChoice?.value);
						const singlePointsEncrypted = matchingPoints?.pointValue;
						if (!singlePointsEncrypted) continue;

						const decryptedPointValue = Number(await decrypt(encryptionKey, singlePointsEncrypted));
						pointMapping.push({
							questionName: answer.questionName,
							value: decryptedPointValue,
							type: matchingPoints.pointType
						});
						break;
					case FormQuestionTypes.MultiChoice:
					case FormQuestionTypes.MultiChoiceWithOther:
						const matchingChoices = meta.choices?.filter(c => {
							const parsedAnswer = tryParseJSONObject(answer.answer?.answerJson);
							return parsedAnswer?.includes(c.value);
						});
						const multiPointsEncrypted = (matchingChoices ?? [])?.map(c => {
							const matchingPoints = meta.points?.pointMappings?.find(p => p.requiredValue === c?.value);
							return matchingPoints?.pointValue;
						});

						if (!multiPointsEncrypted?.length) continue;

						let multiPointValue = 0;
						for (const p of multiPointsEncrypted) {
							const decryptedPointValue = Number(await decrypt(encryptionKey, p));
							multiPointValue += decryptedPointValue;
						}

						// TODO In future - Support multiple point types if required
						// For now assuming all the same/default
						const pointType = meta.points?.pointMappings?.find(p => !!p.pointType)?.pointType;
						pointMapping.push({ questionName: answer.questionName, value: multiPointValue, type: pointType });
						break;

					case FormQuestionTypes.Number:
						const numberPoints = meta.points?.pointMappings?.[0];
						const numberAnswer = answer.answer?.answerString;
						let numberPointVal: string | undefined;
						let decryptedNumberPointValue: number | undefined;
						if (meta.points?.pointMatching === 'equivalent') {
							decryptedNumberPointValue = !strIsNullOrEmpty(numberAnswer) ? Number(numberAnswer) : undefined;
						} else if (meta.points.pointMatching === 'match') {
							if (numberAnswer === numberPoints?.requiredValue) {
								numberPointVal = numberPoints?.pointValue;
							}
							decryptedNumberPointValue = Number(await decrypt(encryptionKey, numberPointVal));
						}
						if (!decryptedNumberPointValue || isNaN(decryptedNumberPointValue)) continue;
						pointMapping.push({
							questionName: answer.questionName,
							value: decryptedNumberPointValue,
							type: numberPoints?.pointType || DEFAULT_POINT_TYPE
						});
						break;
				}
			} catch (e) {
				console.error(e);
			}
		}
	}
	return pointMapping;
};
